import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, ArrowRightLeft, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const TransferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTransfer();
  }, [id]);

  const fetchTransfer = async () => {
    try {
      const response = await api.get(`/transfers/${id}`);
      setTransfer(response.data);
    } catch (error) {
      toast.error('Failed to load transfer details');
      navigate('/transfers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this transfer?')) return;

    setDeleting(true);
    try {
      await api.delete(`/transfers/${id}`);
      toast.success('Transfer deleted successfully');
      navigate('/transfers');
    } catch (error) {
      toast.error('Failed to delete transfer');
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      interested: 'bg-blue-500/20 text-blue-400',
      negotiating: 'bg-white/20 text-white',
      offer_made: 'bg-orange-500/20 text-orange-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <Link
            to="/transfers"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span className="uppercase tracking-wide text-sm">Back to Transfers</span>
          </Link>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white">
                  {transfer.player_name}
                </h1>
                <ArrowRightLeft className="w-8 h-8 text-white" strokeWidth={1.5} />
                <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white">
                  {transfer.club_name}
                </h1>
              </div>
              <span className={`inline-block px-4 py-2 rounded text-sm font-bold uppercase ${getStatusColor(transfer.status)}`}>
                {transfer.status.replace('_', ' ')}
              </span>
            </div>
            <div className="flex gap-3">
              <Link
                to={`/transfers/${id}/edit`}
                data-testid="edit-transfer-button"
                className="inline-flex items-center gap-2 bg-white/10 text-white hover:bg-white/20 border border-white/5 rounded-sm px-4 py-2 transition-all duration-200"
              >
                <Edit className="w-4 h-4" strokeWidth={1.5} />
                Edit
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleting}
                data-testid="delete-transfer-button"
                className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-sm px-4 py-2 transition-all duration-200 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Transfer Details */}
            <div className="glass-card p-6">
              <h2 className="text-2xl font-bold uppercase tracking-tight text-white mb-6 flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6 text-white" strokeWidth={1.5} />
                Transfer Details
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Player</p>
                  <Link to={`/players/${transfer.player_id}`} className="text-xl font-bold text-white hover:text-[#E5E5E5]">
                    {transfer.player_name}
                  </Link>
                </div>
                <div className="p-4 bg-white/5 rounded-lg">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Club</p>
                  <Link to={`/clubs/${transfer.club_id}`} className="text-xl font-bold text-blue-400 hover:text-blue-300">
                    {transfer.club_name}
                  </Link>
                </div>
              </div>
            </div>

            {/* Notes */}
            {transfer.notes && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4">Notes</h3>
                <p className="text-zinc-300 leading-relaxed">{transfer.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Transfer Fee */}
            {transfer.transfer_fee && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" strokeWidth={1.5} />
                  Transfer Fee
                </h3>
                <p className="text-3xl font-bold tabular-nums text-green-400">
                  £{(transfer.transfer_fee / 1000000).toFixed(2)}M
                </p>
              </div>
            )}

            {/* Transfer Date */}
            {transfer.transfer_date && (
              <div className="glass-card p-6">
                <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Transfer Date</h3>
                <p className="text-lg font-bold text-white">{transfer.transfer_date}</p>
              </div>
            )}

            {/* Status */}
            <div className="glass-card p-6">
              <h3 className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Status</h3>
              <span className={`inline-block px-4 py-2 rounded text-sm font-bold uppercase ${getStatusColor(transfer.status)}`}>
                {transfer.status.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferDetail;